// Which extension THIS PAGE talks to (plan §8.1, CLAUDE.md 6).
//
// The SPA is the side that gets this wrong, which is why the resolver lives in
// `packages/protocol` and is called from here: a woo.io page cannot ask the browser which
// extension it is talking to, so it reads whatever it was told and messages that id.
//
// ⚠️ The last source is the trap. `viewModel.extensionId` is the LEGACY extension's
// hardcoded id (`Constants.java`), and during parallel launch both extensions are installed
// — so falling back to it does not fail, it succeeds against the wrong extension. A
// top-level SPA tab has no injected `#woo-extension-id` node and reaches that fallback every
// time, which is exactly the case §8.1 warns about.
import { EXTENSION_ID_NODE, resolveExtensionId, type ResolvedExtensionId } from '@woo-ext/protocol'
import type { ExtensionViewModel } from '@woo-ext/api'

/**
 * Resolves the target, in order: the content script's DOM node, this build's
 * `VITE_EXTENSION_ID`, then the bootstrap's legacy id.
 */
export function extensionTarget(viewModel: ExtensionViewModel): ResolvedExtensionId {
  const node = document.getElementById(EXTENSION_ID_NODE)
  return resolveExtensionId({
    fromDom: node?.textContent?.trim() ?? node?.dataset.extensionId,
    fromBuild: import.meta.env.VITE_EXTENSION_ID as string | undefined,
    fromViewModel: typeof viewModel.extensionId === 'string' ? viewModel.extensionId : undefined,
  })
}

/**
 * The same thing, with the legacy fallback surfaced rather than swallowed.
 *
 * Phase 6.3 gives the content script something to inject the node with and Phase 7 gives
 * the build an id; until then every top-level tab legitimately lands on the fallback, so
 * this warns once instead of failing — a thrown error here would break a page that works.
 */
export function extensionTargetId(viewModel: ExtensionViewModel): string | undefined {
  const resolved = extensionTarget(viewModel)
  if (resolved.isLegacyFallback) {
    console.warn(
      'Messaging the LEGACY extension id from the view model — no #woo-extension-id node and no ' +
        'VITE_EXTENSION_ID. During parallel launch this reaches the wrong extension (plan §8.1).',
    )
  }
  return resolved.id
}
